import React, { useState, useEffect, useRef, useCallback } from 'react';
import Webcam from 'react-webcam';
import { API_BASE_URL } from '../config';
import TallasVestuario from './TallasVestuario';
import ContratistaSelector from './ContratistaSelector';

export default function PersonalManager({ usuario, contratistaId = null, contratistaNombre = '', onBack = null, onMensajePersonal = null }) {
    const [personal, setPersonal] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editingPerson, setEditingPerson] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [columnFilters, setColumnFilters] = useState({
        contratista: '',
        dni: '',
        cargo: '',
        nombre: '',
        contacto: '',
        sucursal: '',
        estado: ''
    });
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [expedienteData, setExpedienteData] = useState([]);
    const [loadingExpediente, setLoadingExpediente] = useState(false);
    const [uploadingDoc, setUploadingDoc] = useState(null);
    const [cargos, setCargos] = useState([]);
    const [contratistas, setContratistas] = useState([]);
    const [selectedContratistaFilter, setSelectedContratistaFilter] = useState('');
    const [isGlobalMode, setIsGlobalMode] = useState(false);
    const [sucursales, setSucursales] = useState([]);

    // Estados para la foto
    const [showFotoModal, setShowFotoModal] = useState(false);
    const [fotoData, setFotoData] = useState(null);
    const [loadingFoto, setLoadingFoto] = useState(false);
    const [modoCaptura, setModoCaptura] = useState(''); // 'camara', 'archivo', 'enviando'
    const [fotoFormulario, setFotoFormulario] = useState(null); // Foto en el formulario de edición
    const webcamRef = useRef(null);

    const isMobileDevice = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');

    // Estados para tallas de vestuario
    const [showTallasModal, setShowTallasModal] = useState(false);
    const [personaTallasId, setPersonaTallasId] = useState(null);

    // Dropdown de acciones por fila
    const [openMenu, setOpenMenu] = useState(null);
    const menuRef = useRef(null);

    const [formData, setFormData] = useState({
        dni: '',
        nombre: '',
        apellido: '',
        email: '',
        telefono: '',
        id_cargo: '',
        estado: 'A',
        id_sucursal: ''
    });

    const targetContratistaId = contratistaId || usuario.id_contratista;
    const isSuperadmin = usuario?.rol_nombre === 'superadmin' || (!usuario?.id_contratista && usuario?.tipo !== 'C');

    useEffect(() => {
        if (isSuperadmin && !contratistaId) {
            setIsGlobalMode(true);
            fetchContratistas();
            fetchPersonal();
            fetchCargos();
        } else if (targetContratistaId) {
            setIsGlobalMode(false);
            fetchPersonal();
            fetchCargos();
            fetchSucursales(targetContratistaId);
        }
    }, [targetContratistaId, isSuperadmin]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpenMenu(null);
            }
        };
        if (openMenu) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [openMenu]);

    const menuActionStyle = {
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '10px 14px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontSize: '0.85rem',
        color: 'var(--text)',
        whiteSpace: 'nowrap',
        transition: 'background 0.15s ease'
    };

    const columnFilterStyle = {
        width: '100%',
        padding: '4px 8px',
        fontSize: '0.8rem',
        border: '1px solid var(--border-light)',
        borderRadius: '4px',
        background: '#fff',
        boxSizing: 'border-box'
    };

    const fetchWithAuth = async (url, options = {}) => {
        const token = localStorage.getItem('authToken');
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`
            }
        });
    };

    const fetchCargos = async () => {
        try {
            let url = `${API_BASE_URL}/api/cargos`;

            // Si es contratista, usar id_contratista
            if (usuario.tipo === 'C' && usuario.id_contratista) {
                url += `?id_contratista=${usuario.id_contratista}`;
            } else if (usuario.empresa_id) {
                // Si es empresa, usar empresa_id
                url += `?id_empresa=${usuario.empresa_id}`;
            }

            if (url !== `${API_BASE_URL}/api/cargos`) {
                const response = await fetchWithAuth(url);
                if (response.ok) {
                    setCargos(await response.json());
                }
            }
        } catch (err) {
            console.error('Error al cargar cargos', err);
        }
    };

    const fetchSucursales = async (contratistaId) => {
        if (!contratistaId) { setSucursales([]); return; }
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/contratistas/${contratistaId}/sucursales`);
            if (response.ok) {
                const data = await response.json();
                setSucursales(data.filter(s => s.activo));
            }
        } catch (err) {
            console.error('Error al cargar sucursales', err);
        }
    };

    const fetchContratistas = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/contratistas?empresa_id=${usuario.empresa_id}`);
            if (response.ok) setContratistas(await response.json());
        } catch (err) { console.error(err); }
    };

    const fetchPersonal = async () => {
        setLoading(true);
        try {
            const useGlobal = isSuperadmin && !contratistaId;
            const url = useGlobal
                ? `${API_BASE_URL}/api/personal-contratistas`
                : `${API_BASE_URL}/api/personal-contratistas/${targetContratistaId}`;
            const response = await fetchWithAuth(url);
            if (response.ok) {
                setPersonal(await response.json());
                setError('');
            } else {
                setError('Error al cargar la nómina de personal');
            }
        } catch (err) {
            setError('Error de conexión al cargar personal');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const normalizedIdCargo = formData.id_cargo || null;
            const url = editingPerson
                ? `${API_BASE_URL}/api/personal-contratistas/${editingPerson.relacion_id}`
                : `${API_BASE_URL}/api/personal-contratistas`;

            const method = editingPerson ? 'PUT' : 'POST';

            const response = await fetchWithAuth(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, id_cargo: normalizedIdCargo, id_contratista: targetContratistaId })
            });

            if (response.ok) {
                await fetchPersonal();
                setShowForm(false);
                setEditingPerson(null);
                setFormData({ dni: '', nombre: '', apellido: '', email: '', telefono: '', id_cargo: '', estado: 'A', id_sucursal: '' });
                setFotoFormulario(null);
            } else {
                const data = await response.json();
                setError(data.error || 'Error al guardar');
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = async (p) => {
        setEditingPerson(p);
        setFormData({
            dni: p.dni,
            nombre: p.nombre,
            apellido: p.apellido || '',
            email: p.email || '',
            telefono: p.telefono || '',
            id_cargo: p.id_cargo || '',
            estado: p.estado || 'A',
            id_sucursal: p.id_sucursal || ''
        });
        // En modo global, cargar las sucursales del contratista de esta persona
        if (isGlobalMode && p.id_contratista) {
            fetchSucursales(p.id_contratista);
        }
        setShowForm(true);
        
        // Cargar la foto de la persona
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/personal/${p.id}/foto`);
            if (response.ok) {
                const data = await response.json();
                if (data.foto) {
                    setFotoFormulario(data.foto);
                }
            }
        } catch (err) {
            console.error('Error al cargar foto en formulario:', err);
        }
    };

    const handleDelete = async (relacionId) => {
        if (!window.confirm('¿Estás seguro de desvincular a esta persona?')) return;
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/personal-contratistas/${relacionId}`, { method: 'DELETE' });
            if (response.ok) {
                fetchPersonal();
            } else {
                setError('Error al eliminar');
            }
        } catch (err) {
            setError('Error de conexión al eliminar');
        }
    };

    const handleRevokeConsent = async (person) => {
        if (!window.confirm(`¿Revocar el consentimiento biométrico de ${person.nombre} ${person.apellido || ''}?\n\nEsto impedirá el procesamiento de datos biométricos (Ley 21.719).`)) return;
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/consentimientos/${person.id}/revocar`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tipo_dato: 'biometria' })
            });
            if (response.ok) {
                alert('Consentimiento revocado exitosamente');
            } else {
                const data = await response.json();
                alert(data.error || 'Error al revocar consentimiento');
            }
        } catch (err) {
            alert('Error de conexión');
        }
    };

    const fetchExpediente = async (person, filterClase = null) => {
        setLoadingExpediente(true);
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/documentos/entidad/P/${person.id}?empresa_id=${usuario.empresa_id}`);
            if (response.ok) {
                let data = await response.json();
                if (filterClase) {
                    data = data.filter(doc => doc.clase === filterClase);
                }
                setExpedienteData(data);
            }
        } catch (err) {
            console.error('Error al cargar expediente:', err);
        } finally {
            setLoadingExpediente(false);
        }
    };

    const handleOpenExpediente = (person) => {
        setSelectedPerson(person);
        fetchExpediente(person);
    };

    const handleUploadDocument = async (tipoId, file, nombreDoc) => {
        if (!file) return;

        setUploadingDoc(tipoId);
        const data = new FormData();
        data.append('archivo', file);
        data.append('titulo', `${nombreDoc} - ${selectedPerson.nombre} ${selectedPerson.apellido}`);
        data.append('tipo_documento_id', tipoId);
        data.append('id_contratista', targetContratistaId);
        data.append('entidad_id', selectedPerson.id);
        data.append('entidad_tipo', 'P');

        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/documentos`, {
                method: 'POST',
                body: data
            });

            if (response.ok) {
                alert('Documento subido y analizado con éxito');
                fetchExpediente(selectedPerson);
                fetchPersonal();
            } else {
                const errorData = await response.json();
                alert(errorData.error || 'Error al subir');
            }
        } catch (err) {
            alert('Error de conexión');
        } finally {
            setUploadingDoc(null);
        }
    };

    const handleDownloadTemplate = async () => {
        const token = localStorage.getItem('authToken');
        const url = `${API_BASE_URL}/api/personal/template?id_contratista=${targetContratistaId}`;
        try {
            const res = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                alert(data.error || 'Error al descargar la plantilla');
                return;
            }
            const blob = await res.blob();
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = 'plantilla_nomina_personal.xlsx';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(a.href);
        } catch (err) {
            alert('Error de conexión al descargar la plantilla');
        }
    };

    const handleImportExcel = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('archivo', file);

        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/api/personal/import/${targetContratistaId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const data = await response.json();
            if (response.ok) {
                alert(data.mensaje);
                fetchPersonal();
            } else {
                setError(data.error || 'Error al importar personal');
            }
        } catch (err) {
            setError('Error de conexión al importar personal');
        } finally {
            setLoading(false);
            e.target.value = ''; // Reset input
        }
    };

    const handleOpenFotoModal = async (person) => {
        setSelectedPerson(person);
        setFotoData(null);
        setModoCaptura(isMobileDevice ? 'camara' : '');
        setShowFotoModal(true);
        setLoadingFoto(true);
        try {
            // Primero verificar si existe foto (rápido)
            const checkResponse = await fetchWithAuth(`${API_BASE_URL}/api/personal/${person.id}/foto`);
            if (checkResponse.ok) {
                const checkData = await checkResponse.json();
                console.log('Verificación de foto:', checkData);
                
                // Si existe foto, obtener los datos completos
                if (checkData.tiene_foto) {
                    const dataResponse = await fetchWithAuth(`${API_BASE_URL}/api/personal/${person.id}/foto/datos`);
                    if (dataResponse.ok) {
                        const data = await dataResponse.json();
                        if (data.foto) {
                            setFotoData(data.foto);
                            console.log('Foto cargada exitosamente');
                        }
                    }
                } else {
                    console.log('No hay foto guardada para esta persona');
                }
            } else {
                console.error('Error en respuesta:', checkResponse.status, checkResponse.statusText);
            }
        } catch (err) {
            console.error('Error al obtener foto:', err);
        } finally {
            setLoadingFoto(false);
        }
    };

    const handleCapture = useCallback(() => {
        if (webcamRef.current) {
            const imageSrc = webcamRef.current.getScreenshot();
            setFotoData(imageSrc);
            setModoCaptura('');
        }
    }, [webcamRef]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFotoData(reader.result);
                setModoCaptura('');
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCloseFotoModal = () => {
        setShowFotoModal(false);
        setSelectedPerson(null);
        setFotoData(null);
        setModoCaptura('');
    };

    const handleSaveFoto = async () => {
        if (!fotoData) return;
        setLoadingFoto(true);
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/personal/${selectedPerson.id}/foto`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fotoBase64: fotoData })
            });
            if (response.ok) {
                alert('Foto guardada exitosamente');
                // Recargar la foto en el formulario si está abierto
                if (editingPerson && selectedPerson && editingPerson.relacion_id === selectedPerson.relacion_id) {
                    const fotoResponse = await fetchWithAuth(`${API_BASE_URL}/api/personal/${selectedPerson.id}/foto`);
                    if (fotoResponse.ok) {
                        const fotoData = await fotoResponse.json();
                        if (fotoData.foto) {
                            setFotoFormulario(fotoData.foto);
                        }
                    }
                }
                handleCloseFotoModal();
            } else {
                alert('Error al guardar la foto');
            }
        } catch (err) {
            alert('Error de conexión');
        } finally {
            setLoadingFoto(false);
        }
    };

    const handleSendLink = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        console.log('1. Iniciando handleSendLink. Persona:', selectedPerson?.nombre);
        
        if (!selectedPerson || !selectedPerson.email) {
            console.log('1a. Detenido: Sin email');
            alert('El trabajador no tiene correo electrónico registrado.');
            return;
        }
        
        console.log('2. Mostrando confirmación para:', selectedPerson.email);
        const confirmado = window.confirm(`¿Enviar enlace de captura al correo: ${selectedPerson.email}?`);
        console.log('3. Resultado confirmación:', confirmado);
        
        if (!confirmado) {
            console.log('3a. Usuario canceló la confirmación');
            return;
        }
        
        console.log('4. Configurando estado enviando...');
        setModoCaptura('enviando');
        
        try {
            const url = `${API_BASE_URL}/api/personal/${selectedPerson.id}/enviar-link-foto`;
            console.log('5. Ejecutando fetchWithAuth hacia:', url);
            
            const response = await fetchWithAuth(url, {
                method: 'POST'
            });
            console.log('6. Respuesta recibida, status:', response.status);
            
            const data = await response.json();
            console.log('7. Datos recibidos:', data);
            
            if (response.ok) {
                alert(data.mensaje);
            } else {
                alert(data.error || 'Error al enviar enlace');
            }
        } catch (err) {
            console.error('8. Error atrapado en catch:', err);
            alert('Error de conexión');
        } finally {
            console.log('9. Finally: restaurando estado');
            setModoCaptura('');
        }
    };

    const filteredPersonal = personal.filter(p => {
        const matchesContratista = !isGlobalMode || !selectedContratistaFilter || p.id_contratista === selectedContratistaFilter;
        if (!matchesContratista) return false;

        const f = columnFilters;
        const val = (s) => (s || '').toLowerCase();
        const matches = (field, filter) => !filter || val(field).includes(filter.toLowerCase());

        if (!matches(p.contratista_nombre, f.contratista)) return false;
        if (!matches(p.dni, f.dni)) return false;
        if (!matches(p.nombre_cargo, f.cargo)) return false;
        const nombreCompleto = `${p.nombre || ''} ${p.apellido || ''}`.trim();
        if (!matches(nombreCompleto, f.nombre)) return false;
        const contacto = `${p.email || ''} ${p.telefono || ''}`.trim();
        if (!matches(contacto, f.contacto)) return false;
        if (!matches(p.nombre_sucursal, f.sucursal)) return false;
        const estadoTexto = p.estado === 'D' ? 'desvinculado' : 'activo';
        if (!matches(estadoTexto, f.estado)) return false;

        return true;
    });

    return (
        <div className="manager-container">
            {onBack && (
                <button onClick={onBack} className="back-button">
                    <span style={{ fontSize: '1.2rem' }}>←</span> Volver al listado
                </button>
            )}

            <div className="header-actions" style={{ marginBottom: '24px', justifyContent: 'flex-end' }}>
                <div className="header-action-buttons" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {usuario.tipo !== 'M' && (
                        <button onClick={handleDownloadTemplate} className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1', minWidth: '140px' }}>
                            📥 Plantilla
                        </button>
                    )}
                    {usuario.tipo !== 'M' && (
                        <label className="add-button" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0, flex: '1', minWidth: '140px', justifyContent: 'center' }}>
                            📤 Importar
                            <input type="file" onChange={handleImportExcel} accept=".xlsx, .xls" style={{ display: 'none' }} />
                        </label>
                    )}
                    <button onClick={() => { setShowForm(true); setEditingPerson(null); setFormData({ dni: '', nombre: '', apellido: '', email: '', telefono: '', id_cargo: '', estado: 'A', id_sucursal: '' }); }} className="add-button" style={{ flex: '1', minWidth: '180px' }}>
                        + Agregar Trabajador
                    </button>
                </div>
            </div>

            {isGlobalMode && (
                <div style={{ marginBottom: '20px' }}>
                    <ContratistaSelector
                        contratistas={contratistas}
                        value={selectedContratistaFilter}
                        onChange={setSelectedContratistaFilter}
                    />
                </div>
            )}

            {error && <div className="error-message" style={{ marginBottom: '20px' }}>{error}</div>}

            {showForm && (
                <div className="modal">
                    <form onSubmit={handleSubmit} className="standard-form" style={{ width: '100%', maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <h3 style={{ marginBottom: '24px', color: 'var(--sidebar-bg)', fontSize: '1.5rem' }}>
                            {editingPerson ? '📝 Editar' : '👷 Nuevo'} Trabajador
                        </h3>

                        {editingPerson && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '20px',
                                padding: '20px',
                                backgroundColor: 'var(--bg-card)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--border-light)',
                                marginBottom: '24px'
                            }}>
                                <div style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '50%',
                                    backgroundColor: '#f1f5f9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    border: '2px solid var(--border-light)',
                                    flexShrink: 0
                                }}>
                                    {fotoFormulario ? (
                                        <img src={fotoFormulario} alt="Perfil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <span style={{ fontSize: '2.5rem' }}>👤</span>
                                    )}
                                </div>
                                <div>
                                    <h4 style={{ marginBottom: '8px', color: 'var(--text-main)' }}>Fotografía del Trabajador</h4>
                                    <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '12px' }}>
                                        Gestiona la foto de perfil para la credencial.
                                    </p>
                                    <button 
                                        type="button"
                                        onClick={() => {
                                            setShowForm(false);
                                            const person = personal.find(p => p.relacion_id === editingPerson.relacion_id);
                                            if (person) handleOpenFotoModal(person);
                                        }} 
                                        className="btn-secondary"
                                        style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                                    >
                                        📷 Gestionar Foto
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                            <div className="form-group">
                                <label>DNI / RUT</label>
                                <input
                                    value={formData.dni}
                                    onChange={e => setFormData({ ...formData, dni: e.target.value })}
                                    placeholder="12.345.678-9"
                                    required
                                    disabled={!!editingPerson}
                                />
                            </div>
                            <div className="form-group">
                                <label>Cargo</label>
                                <select
                                    value={formData.id_cargo}
                                    onChange={e => setFormData({ ...formData, id_cargo: e.target.value })}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '6px',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '1rem'
                                    }}
                                >
                                    <option value="">Seleccione un cargo...</option>
                                    {cargos.map(c => (
                                        <option key={c.id_cargo} value={c.id_cargo}>
                                            {c.cargo}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Nombre</label>
                                <input
                                    value={formData.nombre}
                                    onChange={e => setFormData({ ...formData, nombre: e.target.value })}
                                    placeholder="Nombres"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Apellido</label>
                                <input
                                    value={formData.apellido}
                                    onChange={e => setFormData({ ...formData, apellido: e.target.value })}
                                    placeholder="Apellidos"
                                />
                            </div>
                            <div className="form-group">
                                <label>Email</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="email@ejemplo.com"
                                />
                            </div>
                            <div className="form-group">
                                <label>Teléfono</label>
                                <input
                                    value={formData.telefono}
                                    onChange={e => setFormData({ ...formData, telefono: e.target.value })}
                                    placeholder="+56 9..."
                                />
                            </div>
                            <div className="form-group">
                                <label>Estado</label>
                                <select
                                    value={formData.estado}
                                    onChange={e => setFormData({ ...formData, estado: e.target.value })}
                                    required
                                >
                                    <option value="A">Activo</option>
                                    <option value="D">Desvinculado</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Sucursal</label>
                                <select
                                    value={formData.id_sucursal}
                                    onChange={e => setFormData({ ...formData, id_sucursal: e.target.value })}
                                >
                                    <option value="">Sin sucursal asignada</option>
                                    {sucursales.map(s => (
                                        <option key={s.id} value={s.id}>{s.sucursal}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-actions" style={{ marginTop: '32px' }}>
                            <button type="button" onClick={() => {
                                setShowForm(false);
                                setFotoFormulario(null);
                            }} className="btn-secondary" style={{ marginRight: 'auto' }}>
                                Cancelar
                            </button>
                            <button type="submit" disabled={loading} className="add-button" style={{ padding: '12px 40px' }}>
                                {editingPerson ? 'Actualizar Trabajador' : 'Registrar Trabajador'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {selectedPerson && (
                <div className="modal">
                    <div className="standard-form" style={{ width: '100%', maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                            <div>
                                <h3 style={{ color: 'var(--sidebar-bg)', fontSize: '1.5rem', marginBottom: '4px' }}>
                                    📂 Expediente: {selectedPerson.nombre} {selectedPerson.apellido}
                                </h3>
                                <p style={{ color: 'var(--text-muted)' }}>RUN/DNI: {selectedPerson.dni} | Cargo: {selectedPerson.nombre_cargo || '---'}</p>
                            </div>
                            <button onClick={() => setSelectedPerson(null)} className="btn-secondary" style={{ padding: '8px' }}>✕</button>
                        </div>

                        {loadingExpediente ? <p>Cargando documentos requeridos...</p> : (
                            <table className="standard-table">
                                <thead>
                                    <tr>
                                        <th>Documento Requerido</th>
                                        <th>Estado</th>
                                        <th>Última Carga</th>
                                        <th>Vencimiento</th>
                                        <th>Acción / Resultado IA</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expedienteData.map(doc => (
                                        <tr key={doc.tipo_id}>
                                            <td data-label="Documento" style={{ fontWeight: '500' }}>
                                                {doc.nombre}
                                                {doc.descripcion && <div style={{ fontSize: '0.75rem', fontWeight: '400', color: 'var(--text-muted)' }}>{doc.descripcion}</div>}
                                            </td>
                                            <td data-label="Estado">
                                                <span className={`status-badge ${doc.estado.toLowerCase()}`} style={{ fontSize: '0.7rem' }}>
                                                    {doc.estado}
                                                </span>
                                            </td>
                                            <td data-label="Última Carga" style={{ fontSize: '0.85rem' }}>
                                                {doc.fecha ? new Date(doc.fecha).toLocaleDateString() : '---'}
                                            </td>
                                            <td data-label="Vencimiento" style={{ fontSize: '0.85rem', color: doc.estado === 'VENCIDO' ? '#ea580c' : 'inherit', fontWeight: doc.estado === 'VENCIDO' ? '600' : '400' }}>
                                                {doc.vencimiento ? new Date(doc.vencimiento).toLocaleDateString() : '---'}
                                            </td>
                                            <td data-label="Acciones">
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                                                    {doc.detalles && (
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', maxWidth: '250px', fontStyle: 'italic', textAlign: 'right' }}>
                                                            "{doc.detalles}"
                                                        </div>
                                                    )}

                                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                        <input
                                                            type="file"
                                                            accept=".pdf,.png,.jpg,.jpeg"
                                                            id={`upload-${doc.tipo_id}`}
                                                            style={{ display: 'none' }}
                                                            onChange={(e) => handleUploadDocument(doc.tipo_id, e.target.files[0], doc.nombre)}
                                                        />
                                                        {isMobileDevice && (
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                capture="environment"
                                                                id={`camera-upload-${doc.tipo_id}`}
                                                                style={{ display: 'none' }}
                                                                onChange={(e) => handleUploadDocument(doc.tipo_id, e.target.files[0], doc.nombre)}
                                                            />
                                                        )}
                                                        <button
                                                            disabled={uploadingDoc !== null}
                                                            onClick={() => document.getElementById(`upload-${doc.tipo_id}`).click()}
                                                            className="btn-action btn-edit"
                                                            style={{ fontSize: '0.75rem', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                                                        >
                                                            {uploadingDoc === doc.tipo_id ? (
                                                                <>
                                                                    <span className="spinner" style={{ width: '10px', height: '10px', borderWidth: '2px', marginRight: '0' }}></span>
                                                                    Procesando...
                                                                </>
                                                            ) : (doc.documento_id ? '🔄 Reemplazar' : '📤 Subir')}
                                                        </button>
                                                        {isMobileDevice && (
                                                            <button
                                                                disabled={uploadingDoc !== null}
                                                                onClick={() => document.getElementById(`camera-upload-${doc.tipo_id}`).click()}
                                                                className="btn-action btn-details"
                                                                style={{ fontSize: '0.75rem', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                                                                title="Tomar foto desde la cámara"
                                                            >
                                                                📷 Cámara
                                                            </button>
                                                        )}

                                                        {doc.nombre_archivo && (
                                                            <a
                                                                href={`${API_BASE_URL}/uploads/${doc.nombre_archivo}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                style={{ fontSize: '0.75rem', color: 'var(--primary)', fontWeight: '600', whiteSpace: 'nowrap' }}
                                                            >
                                                                📂 Ver Documento
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        <div className="form-actions" style={{ marginTop: '24px' }}>
                            <button type="button" onClick={() => setSelectedPerson(null)} className="btn-secondary">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showFotoModal && selectedPerson && (
                <div className="modal">
                    <div className="standard-form" style={{ width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                            <div>
                                <h3 style={{ color: 'var(--sidebar-bg)', fontSize: '1.5rem', marginBottom: '4px' }}>
                                    📷 Foto de Perfil
                                </h3>
                                <p style={{ color: 'var(--text-muted)' }}>{selectedPerson.nombre} {selectedPerson.apellido}</p>
                            </div>
                            <button onClick={handleCloseFotoModal} className="btn-secondary" style={{ padding: '8px' }}>✕</button>
                        </div>

                        {loadingFoto ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                                <p>Cargando foto...</p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                                
                                {/* Vista previa de la foto actual o la nueva captura */}
                                <div style={{
                                    width: '200px',
                                    height: '200px',
                                    borderRadius: '50%',
                                    backgroundColor: '#f1f5f9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    border: '4px solid var(--border-light)',
                                    boxShadow: 'var(--shadow-md)',
                                    position: 'relative'
                                }}>
                                    {fotoData ? (
                                        <img src={fotoData} alt="Perfil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <span style={{ fontSize: '5rem', color: '#cbd5e1' }}>👤</span>
                                    )}
                                </div>

                                {/* Opciones de captura */}
                                {!modoCaptura && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                                        <button onClick={() => setModoCaptura('camara')} className="btn-secondary" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                                            <span>💻</span> Tomar Foto con Cámara
                                        </button>
                                        <label className="btn-secondary" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0 }}>
                                            <span>📁</span> Subir desde Archivo
                                            <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                                        </label>
                                        <button onClick={handleSendLink} className="add-button" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                                            <span>✉️</span> Enviar Link de Auto-Registro
                                        </button>
                                    </div>
                                )}

                                {/* Modo Cámara Activo */}
                                {modoCaptura === 'camara' && (
                                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
                                        <div style={{ borderRadius: '16px', overflow: 'hidden', border: '2px solid var(--border-light)' }}>
                                            <Webcam
                                                audio={false}
                                                ref={webcamRef}
                                                playsInline
                                                mirrored
                                                screenshotFormat="image/jpeg"
                                                width={isMobileDevice ? '100%' : 300}
                                                height={isMobileDevice ? '100%' : 300}
                                                style={{ width: isMobileDevice ? '100%' : 300, maxWidth: '100%', display: 'block' }}
                                                videoConstraints={{ facingMode: { ideal: 'user' } }}
                                            />
                                        </div>
                                        <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                                            <button onClick={() => setModoCaptura('')} className="btn-secondary" style={{ flex: 1 }}>Cancelar</button>
                                            <button onClick={handleCapture} className="add-button" style={{ flex: 1 }}>📸 Capturar</button>
                                        </div>
                                    </div>
                                )}

                                {/* Modo Enviando Link */}
                                {modoCaptura === 'enviando' && (
                                    <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>
                                        <span className="spinner" style={{ display: 'inline-block', marginBottom: '12px' }}></span>
                                        <p>Enviando correo al trabajador...</p>
                                    </div>
                                )}

                                {/* Guardar Cambios si hay nueva foto (y no estamos en modo cámara) */}
                                {fotoData && !modoCaptura && (
                                    <div style={{ marginTop: '16px', width: '100%', borderTop: '1px solid var(--border-light)', paddingTop: '20px', display: 'flex', gap: '12px' }}>
                                        <button onClick={() => setFotoData(null)} className="btn-secondary" style={{ flex: 1 }}>
                                            Quitar Foto
                                        </button>
                                        <button onClick={handleSaveFoto} className="add-button" style={{ flex: 1 }}>
                                            Guardar Foto
                                        </button>
                                    </div>
                                )}

                            </div>
                        )}
                    </div>
                </div>
            )}

            {loading && personal.length === 0 ? <p>Cargando personal...</p> : (
                <table className="standard-table">
                    <thead>
                        <tr>
                            {isGlobalMode && <th>Contratista</th>}
                            <th>DNI / RUT</th>
                            <th>Cargo</th>
                            <th>Nombre Completo</th>
                            <th>Información de Contacto</th>
                            <th>Documentación</th>
                            <th>Sucursal</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                        <tr>
                            {isGlobalMode && (
                                <th style={{ padding: '4px 8px' }}>
                                    <input
                                        type="text"
                                        placeholder="Filtrar..."
                                        value={columnFilters.contratista}
                                        onChange={e => setColumnFilters({ ...columnFilters, contratista: e.target.value })}
                                        style={columnFilterStyle}
                                    />
                                </th>
                            )}
                            <th style={{ padding: '4px 8px' }}>
                                <input
                                    type="text"
                                    placeholder="Filtrar..."
                                    value={columnFilters.dni}
                                    onChange={e => setColumnFilters({ ...columnFilters, dni: e.target.value })}
                                    style={columnFilterStyle}
                                />
                            </th>
                            <th style={{ padding: '4px 8px' }}>
                                <input
                                    type="text"
                                    placeholder="Filtrar..."
                                    value={columnFilters.cargo}
                                    onChange={e => setColumnFilters({ ...columnFilters, cargo: e.target.value })}
                                    style={columnFilterStyle}
                                />
                            </th>
                            <th style={{ padding: '4px 8px' }}>
                                <input
                                    type="text"
                                    placeholder="Filtrar..."
                                    value={columnFilters.nombre}
                                    onChange={e => setColumnFilters({ ...columnFilters, nombre: e.target.value })}
                                    style={columnFilterStyle}
                                />
                            </th>
                            <th style={{ padding: '4px 8px' }}>
                                <input
                                    type="text"
                                    placeholder="Filtrar..."
                                    value={columnFilters.contacto}
                                    onChange={e => setColumnFilters({ ...columnFilters, contacto: e.target.value })}
                                    style={columnFilterStyle}
                                />
                            </th>
                            <th style={{ padding: '4px 8px' }}></th>
                            <th style={{ padding: '4px 8px' }}>
                                <input
                                    type="text"
                                    placeholder="Filtrar..."
                                    value={columnFilters.sucursal}
                                    onChange={e => setColumnFilters({ ...columnFilters, sucursal: e.target.value })}
                                    style={columnFilterStyle}
                                />
                            </th>
                            <th style={{ padding: '4px 8px' }}>
                                <input
                                    type="text"
                                    placeholder="Filtrar..."
                                    value={columnFilters.estado}
                                    onChange={e => setColumnFilters({ ...columnFilters, estado: e.target.value })}
                                    style={columnFilterStyle}
                                />
                            </th>
                            <th style={{ padding: '4px 8px' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPersonal.map(p => (
                            <tr key={p.relacion_id}>
                                {isGlobalMode && <td data-label="Contratista" style={{ fontWeight: '600', fontSize: '0.85rem' }}>{p.contratista_nombre || '---'}</td>}
                                <td data-label="DNI / RUT" style={{ fontWeight: '700', color: 'var(--primary)' }}>{p.dni}</td>
                                <td data-label="Cargo" style={{ fontWeight: '600', color: 'var(--text-muted)' }}>{p.nombre_cargo || '---'}</td>
                                <td data-label="Nombre">
                                    <div style={{ fontWeight: '600' }}>{p.nombre} {p.apellido}</div>
                                </td>
                                <td data-label="Contacto">
                                    {p.email && <div style={{ fontSize: '0.85rem' }}><small>📧</small> {p.email}</div>}
                                    {p.telefono && <div style={{ fontSize: '0.85rem' }}><small>📞</small> {p.telefono}</div>}
                                    {!p.email && !p.telefono && '---'}
                                </td>
                                <td data-label="Documentación">
                                    {p.documentacion ? (() => {
                                        const { aprobados, total, vencidos, rechazados } = p.documentacion;
                                        const pct = total > 0 ? Math.round((aprobados / total) * 100) : 0;
                                        const barColor = pct === 0 ? '#7c3aed'
                                            : pct < 50 ? '#dc2626'
                                            : pct < 75 ? '#ea580c'
                                            : pct < 100 ? '#eab308'
                                            : '#16a34a';
                                        return (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'stretch', minWidth: '120px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.7rem' }}>
                                                    <span style={{ fontWeight: '600', color: barColor }}>{pct}%</span>
                                                    <span style={{ color: 'var(--text-muted)' }}>{aprobados}/{total}</span>
                                                </div>
                                                <div style={{ height: '8px', borderRadius: '4px', background: '#e5e7eb', overflow: 'hidden' }}>
                                                    <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: '4px', transition: 'width 0.3s ease' }} />
                                                </div>
                                                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                                                    {vencidos > 0 && <span style={{ color: '#ea580c', marginRight: '6px' }}>{vencidos} Venc.</span>}
                                                    {rechazados > 0 && <span style={{ color: 'var(--error)' }}>{rechazados} Rech.</span>}
                                                </div>
                                            </div>
                                        );
                                    })() : (
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Cargando...</span>
                                    )}
                                </td>
                                <td data-label="Sucursal" style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                                    {p.nombre_sucursal || '---'}
                                </td>
                                <td data-label="Estado">
                                    <span className={`status-badge ${p.estado === 'D' ? 'invalid' : 'valid'}`} style={{ fontSize: '0.7rem' }}>
                                        {p.estado === 'D' ? 'Desvinculado' : 'Activo'}
                                    </span>
                                </td>
                                <td data-label="Acciones" style={{ whiteSpace: 'nowrap' }}>
                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                const estimatedHeight = 270;
                                                const openUp = rect.bottom + estimatedHeight > window.innerHeight;
                                                const menuWidth = 196;
                                                setOpenMenu(openMenu?.id === p.relacion_id ? null : {
                                                    id: p.relacion_id,
                                                    top: openUp ? rect.top - estimatedHeight - 8 : rect.bottom + 8,
                                                    left: Math.min(Math.max(8, rect.left), window.innerWidth - menuWidth - 8)
                                                });
                                            }}
                                            className="btn-action btn-details"
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                        >
                                            {(loadingExpediente || uploadingDoc !== null) && selectedPerson?.id === p.id ? (
                                                <span className="spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></span>
                                            ) : '⚙️'}
                                            Acciones
                                            <span style={{ fontSize: '0.7rem', marginLeft: '2px' }}>▾</span>
                                        </button>

                                        {openMenu?.id === p.relacion_id && (
                                            <div
                                                ref={menuRef}
                                                className="personal-actions-menu"
                                                style={{
                                                    position: 'fixed',
                                                    top: openMenu.top,
                                                    left: openMenu.left,
                                                    background: 'var(--bg-card)',
                                                    border: '1px solid var(--border-light)',
                                                    borderRadius: '8px',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                                    zIndex: 9999,
                                                    minWidth: '180px',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <button onClick={() => { handleEdit(p); setOpenMenu(null); }} className="menu-action" style={menuActionStyle}>
                                                    📝 Editar
                                                </button>
                                                <button onClick={() => { handleOpenExpediente(p); setOpenMenu(null); }} className="menu-action" style={menuActionStyle}>
                                                    📂 Expediente
                                                </button>
                                                <button onClick={() => { handleOpenFotoModal(p); setOpenMenu(null); }} className="menu-action" style={menuActionStyle}>
                                                    📷 Foto
                                                </button>
                                                <button onClick={() => { setPersonaTallasId(p.id); setShowTallasModal(true); setOpenMenu(null); }} className="menu-action" style={menuActionStyle}>
                                                    📏 Tallas
                                                </button>
                                                {onMensajePersonal && (
                                                    <button onClick={() => { onMensajePersonal(p.id); setOpenMenu(null); }} className="menu-action" style={menuActionStyle}>
                                                        💬 Mensaje
                                                    </button>
                                                )}
                                                <button onClick={() => { handleRevokeConsent(p); setOpenMenu(null); }} className="menu-action" style={menuActionStyle}>
                                                    🛡️ Revocar consentimiento
                                                </button>
                                                <div style={{ borderTop: '1px solid var(--border-light)', margin: '4px 0' }}></div>
                                                <button onClick={() => { handleDelete(p.relacion_id); setOpenMenu(null); }} className="menu-action" style={menuActionStyle}>
                                                    🗑️ Eliminar
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {filteredPersonal.length === 0 && (
                            <tr><td colSpan={isGlobalMode ? 9 : 8} style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>No se encontraron trabajadores que coincidan con la búsqueda.</td></tr>
                        )}
                    </tbody>
                </table>
            )}

            {showTallasModal && personaTallasId && (
                <TallasVestuario 
                    personaId={personaTallasId}
                    onClose={() => {
                        setShowTallasModal(false);
                        setPersonaTallasId(null);
                    }}
                />
            )}
        </div>
    );
}
